import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Headphones, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { CONTACT } from "@/data/site";
import { useLeadModal } from "@/context/LeadModalProvider";

const SUGGESTIONS = [
  "How much does a cleaning cost?",
  "Do you use green cleaning products?",
  "What areas do you serve?",
  "Do you offer recurring cleanings?",
];

const sessionId = `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;

export const Chatbot = () => {
  const { anyModalOpen } = useLeadModal();
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! 👋 I'm the CJB assistant. Ask me about pricing, scheduling, our Green Cleaning products, service areas in Northern Virginia, and more." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble connecting. Please tap 'Connect to live team' below." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle"
        className={`fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-navy text-white shadow-[0_10px_40px_rgba(27,42,91,0.4)] flex items-center justify-center transition-opacity duration-200 ${anyModalOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
        aria-label="Open chat">
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><X /></motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><MessageCircle /></motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[75vh] rounded-3xl bg-white border border-border shadow-2xl flex flex-col overflow-hidden">
            <div className="bg-navy text-white px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-leaf flex items-center justify-center"><Leaf className="w-5 h-5" /></div>
              <div className="leading-tight">
                <p className="font-heading font-semibold">CJB Assistant</p>
                <p className="text-xs text-white/60">Green Cleaning · Northern Virginia</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-navy text-white rounded-br-md" : "bg-white border border-border text-foreground rounded-bl-md"}`}>
                    {m.content || <span className="inline-flex gap-1 items-center text-muted-foreground"><span className="w-1.5 h-1.5 bg-leaf rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-leaf rounded-full animate-bounce [animation-delay:0.15s]" /><span className="w-1.5 h-1.5 bg-leaf rounded-full animate-bounce [animation-delay:0.3s]" /></span>}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                      className="text-xs px-3 py-1.5 rounded-full border border-navy/15 text-navy hover:bg-navy hover:text-white transition-colors">{s}</button>
                  ))}
                </div>
              )}

              {live && (
                <div className="bg-white border border-leaf/40 rounded-2xl p-4 text-sm space-y-2" data-testid="chat-live-panel">
                  <p className="font-medium text-navy flex items-center gap-2"><Headphones className="w-4 h-4 text-leaf" /> Connect to our live team</p>
                  <p className="text-muted-foreground">Call <a href={CONTACT.phoneHref} className="text-leaf font-medium">{CONTACT.phone}</a> or email <a href={`mailto:${CONTACT.email}`} className="text-leaf font-medium">{CONTACT.email}</a>.</p>
                  <Link to="/contact" onClick={() => setOpen(false)} className="inline-block text-leaf font-medium">Open the contact form →</Link>
                </div>
              )}
            </div>

            <div className="border-t border-border p-3 bg-white">
              <button onClick={() => setLive((v) => !v)} data-testid="chat-live-btn"
                className="w-full mb-2 text-xs font-medium text-navy/70 hover:text-leaf flex items-center justify-center gap-1.5 transition-colors">
                <Headphones className="w-3.5 h-3.5" /> Connect to live team
              </button>
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask a question…"
                  data-testid="chat-input"
                  className="flex-1 h-11 px-4 rounded-full bg-background border border-border text-sm outline-none focus:border-leaf" />
                <button onClick={() => send()} disabled={busy} data-testid="chat-send-btn"
                  className="w-11 h-11 rounded-full bg-leaf text-white flex items-center justify-center disabled:opacity-50 hover:scale-105 transition-transform">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
